import { TournamentVenuesComponent } from '../tournament-venues/tournament-venues.component';
import { Component, DestroyRef, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AlertController, IonicModule, IonRouterOutlet, ModalController, PopoverController } from '@ionic/angular';
import { addHours, format } from 'date-fns';
import { AuthService } from '../../../auth/auth.service';
import { ToastService } from '../../../_core/services/toast.service';
import { TrackersComponent } from '../../../_shared/components/trackers/trackers.component';
import { Tournament } from '../../../_shared/models/tournament.model';
import { TournamentService } from '../../tournament.service';
import { TournamentDetailsService } from '../tournament-details.service';
import { TournamentMenuComponent } from '../tournament-menu/tournament-menu.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { OfficialsComponent } from '../../../_shared/components/officials/officials.component';
import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CreateTournamentComponent } from '../../create-tournament/create-tournament.component';
import { firstValueFrom } from 'rxjs';
import { TournamentPolicyComponent } from '../tournament-policy/tournament-policy.component';
import { TournamentWidgetStatusComponent } from '../tournament-widget-status/tournament-widget-status.component';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { ShareMatchComponentModule } from '../../../_shared/components/share-match/share-match.module';
import { LoaderService } from '../../../_core/services/loader.service';
import { Venue } from '../../../_shared/models/csTypes/venue';
import { TournamentAnnouncementComponent } from '../tournament-announcement/tournament-announcement.component';

@Component({
    selector: 'app-tournament-header',
    templateUrl: './tournament-header.component.html',
    styleUrls: ['./tournament-header.component.scss'],
    standalone: true,
    imports: [
        IonicModule,
        DatePipe,
        TranslateModule,
        ShareMatchComponentModule,
        TournamentAnnouncementComponent,
        FormsModule
    ]
})

export class TournamentHeaderComponent implements OnInit {
    destroyRef = inject(DestroyRef);
    public authService = inject(AuthService);
    private translateService = inject(TranslateService);
    private toastService = inject(ToastService);

    @Input() showToolbar = true;
    @Input() matchType: string;
    @Input() showExportPdf: number;
    @Output() isActive = new EventEmitter<boolean>();
    @Output() changeDate = new EventEmitter<{date: string, fromCache?:boolean}>();
    @Output() changeVenue = new EventEmitter<string>();
    private tournamentId: string;
    tournament: Tournament;
    tournamentDays: string[] = [];
    tournamentVenues: Venue[] = [];
    selectedDay: string;
    isOwner = false;
    isLoading = false;

    isDeleting: boolean;
    canFollow: boolean;
    isTracker: boolean;
    
    // Announcement editing
    isEditingAnnouncement = false;
    editingAnnouncementMessage = '';
    isSavingAnnouncement = false;

    constructor(
        private activatedRoute: ActivatedRoute,
        private tournamentDetailsService: TournamentDetailsService,
        private tournamentService: TournamentService,
        private alertController: AlertController,
        private popoverController: PopoverController,
        private toast: ToastService,
        private modalController: ModalController,
        private routerOutlet: IonRouterOutlet,
        private router: Router,
        private loader: LoaderService
    ) { }

    async ngOnInit() {
        this.tournamentId = this.activatedRoute.snapshot.parent.params.id;
        await this.getTournament(true);
    }

    async getTournament(useCache?:boolean) {
        this.isLoading = true;
        this.tournament = await this.tournamentDetailsService.getTournamentInfo(this.tournamentId);        
        const prevSelectedDate = this.tournamentDetailsService.getSelectedTournamentDate()

        if (!this.tournament?._id) {
            this.router.navigate(['/tournaments']);
        } else {
            this.tournamentVenues = this.tournament?.venues || [];
            this.isOwner = this.tournament.owners.some(owner => this.authService.authUserSignal()?._id === owner);
            this.isTracker = this.tournament.trackers.some(t => this.authService.authUserSignal()?._id === t.tracker?._id);
            this.canFollow = !this.tournament.viewers.some(viewer => this.authService.authUserSignal()?._id === viewer);
            this.tournamentDays = this.getTournamentDays();
            if(prevSelectedDate && prevSelectedDate.length > 0) {
                this.selectedDay = prevSelectedDate
            }else if(this.tournament.startDate && this.tournament.endDate) {
                if (new Date() > new Date(this.tournament.endDate)) {
                    this.selectedDay = this.tournamentDays[this.tournamentDays.length - 1]
                } else if (this.tournamentDays.length > 0 && !this.selectedDay) {
                    if (new Date().getMonth() < new Date(this.tournamentDays[0]).getMonth() ||
                        new Date().getDate() <= new Date(this.tournamentDays[0]).getDate()) {
                        this.selectedDay = this.tournamentDays[0];
                    } else {
                        const index = this.tournamentDays
                            .findIndex(date =>
                                date === format(new Date(), 'yyyy-MM-dd')
                            );

                        this.selectedDay = index > -1
                            ? this.tournamentDays[index]
                            : this.tournamentDays[this.tournamentDays.length - 1];
                    }
                }
            }
            this.isLoading = false;
            /**
             * Fetch data from cache for TC tournaments on the first load (or refresh) to avoid making an API call.
             */
            this.changeDate.next({
                date: this.selectedDay,
                fromCache: this.tournament.partner === 'TC' && (!prevSelectedDate && useCache)
            });
        }

    }

    async openTournamentMenu(ev: any) {
        const popover = await this.popoverController.create({
            component: TournamentMenuComponent,
            componentProps: {
                tournament: this.tournament,
                selectedDate: this.selectedDay,
                showExportPdf: this.showExportPdf
            },
            event: ev,
            translucent: false,
            cssClass: 'mt-popover'
        });

        await popover.present();

        const { data } = await popover.onDidDismiss();

        if (data?.allowResultSubmissionToPartner != null) {
            this.allowResultSubmissionToPartner(data.allowResultSubmissionToPartner);
        }

        if (data?.allowAutoScoreSubmissionToTmx != null) {
            this.allowAutoScoreSubmissionToTmx(data.allowAutoScoreSubmissionToTmx);
        }

        switch (data) {
            case 'delete':
                await this.deleteTournament();
                break;
            case 'update-tournament':
                this.updateTournament();
                break;
            case 'add-tracker':
                await this.viewTrackers();
                break;
            case 'add-official':
                await this.viewOfficials();
                break;
            case 'venues':
                await this.viewVenues();
                break;
            case 'edit':
                await this.edit();
                break;
            case 'view-policy':
                await this.organization();
                break;
            case 'widget-status':
                await this.openWidgetStatus();
                break;
            case 'announcement':
                await this.openAnnouncement();
                break;
            default:
                break;
        }
    }

    async viewVenues() {
        const modal = await this.modalController.create({
            component: TournamentVenuesComponent,
            componentProps: {
                tournament: this.tournament
            }
        });

        await modal.present();
    }

    async edit() {
        const modal = await this.modalController.create({
            component: CreateTournamentComponent,
            componentProps: {
                isEdit: true,
                tournament: this.tournament
            },
            presentingElement: this.routerOutlet.nativeEl
        });

        await modal.present();

        const { data } = await modal.onDidDismiss<{data: Tournament; message: string}>();

        if (data) {
            await this.getTournament();
            this.tournament = data.data;
            this.tournamentDetailsService.tournament = data.data;
            const updatedTournaments = this.tournamentService.tournaments.map(_t => _t._id === this.tournament._id? this.tournament : _t);
            this.tournamentService.publishTournamentsUpdate(updatedTournaments);
            this.toast.presentToast(data.message);
        }
    }

    private async deleteTournament() {
        const alert = await this.alertController.create({
            header: this.translateService.instant('Delete tournament'),
            buttons: [
                {
                    text: this.translateService.instant('Cancel'),
                    role: 'cancel'
                }, {
                    text: this.translateService.instant('Okay'),
                    role: 'destructive',
                    handler: () => {
                        this.isDeleting = true;

                        this.tournamentService
                            .delete(this.tournament._id)
                            .pipe(
                                takeUntilDestroyed(this.destroyRef)
                            )
                            .subscribe({
                                next: (res) => {
                                    this.toast.presentToast(res.message);
                                    this.router.navigate(['/tournaments']);
                                }
                            });
                    }
                }
            ]
        });

        await alert.present();
    }

    private async viewTrackers() {
        const modal = await this.modalController.create({
            component: TrackersComponent,
            componentProps: {
                tournament: this.tournament
            },
            presentingElement: this.routerOutlet.nativeEl
        });

        await modal.present();
    }

    private async viewOfficials() {
        const modal = await this.modalController.create({
            component: OfficialsComponent,
            componentProps: {
                _tournamentId: this.tournament._id,
                isEdit: true,
            },
            presentingElement: this.routerOutlet.nativeEl
        });

        await modal.present();
    }

    private updateTournament() {
        if (this.tournament.spreadsheetId) {
            this.tournamentDetailsService
                .syncWithSpreadsheet(this.tournament._id)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe({
                    next: () => {
                        this.getTournament();
                        this.toast.presentToast('Tournament is updated');
                    }
                });
        } else {
            this.tournamentService
                .createUstaTournament({ tournamentId: this.tournament.tournamentId, partner: this.tournament.partner })
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe({
                    next: () => {
                        this.getTournament();
                        this.toast.presentToast('Tournament is updated');
                    }
                });
        }
    }

    private allowResultSubmissionToPartner(allowResultSubmissionToPartner) {
        this.tournamentDetailsService
            .allowResultSubmissionToPartner(this.tournament._id, allowResultSubmissionToPartner)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (res) => {
                    this.tournament.allowResultSubmissionToPartner = allowResultSubmissionToPartner;
                    this.toast.presentToast(res.message);
                }
            });
    }

    private allowAutoScoreSubmissionToTmx(allowAutoScoreSubmission) {
        this.loader.present();
        this.tournamentDetailsService
            .allowAutoSubmitScore(this.tournament?._id, allowAutoScoreSubmission)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (res) => {
                    this.tournament.allowAutoSubmitScore = allowAutoScoreSubmission;
                    this.tournamentDetailsService.tournament.allowAutoSubmitScore = allowAutoScoreSubmission;
                    if (this.selectedDay) {
                        this.tournamentDetailsService.setSelectedTournamentDate(this.selectedDay);
                    }
                    this.toast.presentToast(res.message);
                    this.getTournament();
                    this.loader.dismiss();
                },
                error: (err) => {
                    this.toast.presentDangerToast(err);
                    this.loader.dismiss();
                }
            });
    }

    changeTournamentVenue(ev) {
        const venueId = ev.detail.value;
        this.changeVenue.next(venueId);
    }


    private getTournamentDays(): string[] {
        const dateArray = [];
        let currentDate = this.tournament.startDate;

        while (currentDate <= this.tournament.endDate) {
            // addHours is used instead of addDays becuase of an issue with FNS library in daylight saving
            dateArray.push(currentDate);
            currentDate = this.addHours(currentDate, 24);
        }

        return dateArray;
    }

    private addHours(currentDate, hoursToAdd): string {
        const newCurrentDate = addHours(new Date(currentDate), hoursToAdd).toISOString();
        return `${newCurrentDate.split('T')[0]}`;
    }

    changeTournamentDate(ev) {
        this.selectedDay = ev?.detail?.value;
        this.changeDate.next({ date: this.selectedDay, fromCache: this.tournament.partner === 'TC'? true: false });
    }

    showHeaderToolbar() {
        return (!this.isLoading && (this.showDays() || this.showVenues()) && this.showToolbar);
    }

    /**
     * 
     * Hide Date Dropdown if it is draw route
     */
    showDays(): boolean {
        const viewType = this.activatedRoute.snapshot.data['type'];
        const isDrawRoute = this.router.url.includes('/draw');
        return this.tournamentDays?.length > 1 && viewType !== 'live-score' && !isDrawRoute;
    }

    showVenues(): boolean {
        return this.tournament?.venues?.length > 0 && this.matchType?.toLowerCase() === 'upcoming';
    }

    isLiveScoreRoute(): boolean {
        return this.router.url.includes('/live-score') || this.router.url.includes('/upcoming');
    }

    changeFollow() {
        if (this.canFollow) {
            this.tournamentDetailsService
                .follow(this.tournamentId)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe({
                    next: res => {
                        this.canFollow = false;
                        this.tournamentDetailsService.tournament.viewers.push(this.authService.authUserSignal()?._id);
                        if (res) {
                            this.toastService.presentToast('You are now following this tournament');
                        }
                    }
                });
        } else {
            this.tournamentDetailsService
                .unfollow(this.tournamentId)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe({
                    next: res => {
                        this.canFollow = true;
                        this.tournament.viewers.forEach((viewer, i) => {
                            if (viewer === this.authService.authUserSignal()?._id) {
                                this.tournamentDetailsService.tournament.viewers.splice(i, 1);
                            }
                        });
                        if (res) {
                            this.toastService.presentToast('Tournament unfollowed successfully');
                        }
                    },
                });
        }
    }

    async organization(){
        const modal = await this.modalController.create({
            component: TournamentPolicyComponent,
            componentProps: {
              tournament: this.tournament,
            },
          });
  
          await modal.present();
  
          const { data } = await modal.onDidDismiss();
    }

    async openWidgetStatus(){
        const modal = await this.modalController.create({
            component: TournamentWidgetStatusComponent,
            componentProps: {
              tournament: this.tournament,
            },
          });
  
          await modal.present();
  
          const { data } = await modal.onDidDismiss();
    }

    async openAnnouncement() {
        const modal = await this.modalController.create({
            component: TournamentAnnouncementComponent,
            componentProps: {
                tournament: this.tournament,
                isOwner: true
            },
            presentingElement: this.routerOutlet.nativeEl
        });

        await modal.present();

        const { data } = await modal.onDidDismiss();
        if (data?.updated) {
            this.tournament.announcement = data.announcement;
        }
    }

    toggleAnnouncement(ev: any) {
        const enabled = ev.detail.checked;
        const message = this.tournament.announcement?.message || '';
        
        this.tournamentDetailsService
            .updateAnnouncement(this.tournament._id, message, enabled)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: () => {
                    this.tournament.announcement = { message, enabled };
                    this.toastService.presentToast(
                        this.translateService.instant(enabled ? 'Announcement enabled' : 'Announcement disabled')
                    );
                },
                error: (err) => {
                    const errorMessage = err.error?.message || this.translateService.instant('Failed to update announcement');
                    this.toastService.presentDangerToast(errorMessage);
                    // Revert toggle state on error
                    this.tournament.announcement = { message, enabled: !enabled };
                }
            });
    }

    startEditingAnnouncement() {
        this.editingAnnouncementMessage = this.tournament.announcement?.message || '';
        this.isEditingAnnouncement = true;
    }

    cancelEditingAnnouncement() {
        this.isEditingAnnouncement = false;
        this.editingAnnouncementMessage = '';
    }

    saveAnnouncementMessage() {
        if (!this.editingAnnouncementMessage.trim()) {
            this.toastService.presentDangerToast(this.translateService.instant('Announcement message cannot be empty'));
            return;
        }

        this.isSavingAnnouncement = true;
        const enabled = this.tournament.announcement?.enabled || false;

        this.tournamentDetailsService
            .updateAnnouncement(this.tournament._id, this.editingAnnouncementMessage, enabled)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: () => {
                    this.tournament.announcement = { message: this.editingAnnouncementMessage, enabled };
                    this.toastService.presentToast(this.translateService.instant('Announcement updated successfully'));
                    this.isEditingAnnouncement = false;
                    this.isSavingAnnouncement = false;
                },
                error: (err) => {
                    const errorMessage = err.error?.message || this.translateService.instant('Failed to update announcement');
                    this.toastService.presentDangerToast(errorMessage);
                    this.isSavingAnnouncement = false;
                }
            });
    }
    

}
